import { MdOutlineClear } from 'react-icons/md';
import { Button, Tab, Tabs } from '@ds';
import { FilterDropdown } from '@ui';
import { CUSTOMER_ROLES_SELECT_LIST } from '@lp/features/admin/forms';
import { CustomerRoleT, CustomerListFilters } from '@/api/apis/customer';
import { useInfiniteTXCentersListQuery } from '@/queries/tx-centers';
import { useAsyncSearchableList } from '@/hooks/useAsyncSearchableList';
import { useSearchableList } from '@/hooks/useSearchableList';
import { UseFiltersType } from '@/hooks/useFilters';
import { NameByValue } from '@/types';
import { BoolFilterKey, boolFilterToKey, keyToBoolFilter } from '@/utils/helpers';
import { FILTERS_NAMES } from '../config';

type TXUsersFiltersProps = UseFiltersType<CustomerListFilters>;

export const TXUsersFilters = ({ filters, hasFilters, updateFilters, clearFilters }: TXUsersFiltersProps) => {
  const { list: roleList, setSearchValue: onRoleSearch } = useSearchableList(CUSTOMER_ROLES_SELECT_LIST, [
    'name',
    'value',
  ]);

  const {
    list: txCenterList,
    isLoading,
    hasNextPage,
    loadNextPage,
    setSearchValue: onLocationSearch,
  } = useAsyncSearchableList({
    query: useInfiniteTXCentersListQuery,
    defaultSortOrder: { orderBy: 'name', asc: true },
  });

  return (
    <div className="flex gap-7 items-center">
      <FilterDropdown<NameByValue<CustomerRoleT>>
        title={FILTERS_NAMES.roles}
        items={roleList}
        selectedKeys={filters.roles || []}
        mode="multiple"
        onSearchValue={onRoleSearch}
        onSelectedKeysChange={(values) => updateFilters(values, 'roles')}
      />
      <FilterDropdown
        title="TX Center Name"
        items={txCenterList as NameByValue<string>[]}
        selectedKeys={filters.txCenters || []}
        mode="multiple"
        hasNextPage={hasNextPage}
        isLoading={isLoading}
        loadNextPage={loadNextPage}
        onSearchValue={onLocationSearch}
        onSelectedKeysChange={(values) => updateFilters(values, 'txCenters')}
      />
      <Tabs
        radius="full"
        aria-label="user primary filter"
        selectedKey={boolFilterToKey(filters.isPrimary)}
        onSelectionChange={(value) => updateFilters(keyToBoolFilter(value as BoolFilterKey), 'isPrimary')}
        classNames={{
          tabList: 'bg-base-50 p-0 gap-0',
          tabContent: 'px-2 text-s-sb group-data-[selected=true]:text-base-90',
          cursor: 'bg-base-5',
        }}
      >
        <Tab key={BoolFilterKey.Undefined} title="All" />
        <Tab key={BoolFilterKey.True} title="Primary contact" />
        <Tab key={BoolFilterKey.False} title="Not a primary contact" />
      </Tabs>
      <Tabs
        radius="full"
        aria-label="employee status filter"
        selectedKey={boolFilterToKey(filters.enabled)}
        onSelectionChange={(value) => updateFilters(keyToBoolFilter(value as BoolFilterKey), 'enabled')}
        classNames={{
          tabList: 'bg-base-50 p-0 gap-0',
          tabContent: 'px-2 text-s-sb group-data-[selected=true]:text-base-90',
          cursor: 'bg-base-5',
        }}
      >
        <Tab key={BoolFilterKey.Undefined} title="All" />
        <Tab key={BoolFilterKey.True} title="Active" />
        <Tab key={BoolFilterKey.False} title="Inactive" />
      </Tabs>
      {hasFilters && (
        <Button startContent={<MdOutlineClear size={18} />} size="sm" variant="light" onClick={clearFilters}>
          Clear all
        </Button>
      )}
    </div>
  );
};
